/**
 * Fixed OAuth scope catalog for Role / Group Management UI (Sprint 6 RBAC).
 * Educational metadata only — authorization still uses token scopes via scopeAuth.js.
 *
 * Keep in sync with:
 * - FrontEnd/frontend/src/lib/scopeAuth.js ALL_SCOPE_NAMES
 * - halo-infra pre-token-gen ALL_SCOPE_NAMES / GROUP_SCOPE_NAMES
 * - sprint-6/RBAC/seed-role-scopes.json
 */

/**
 * @typedef {{ name: string, label: string, description: string, affects: string[], area: string, action?: string }} ScopeCatalogEntry
 */

/** @type {ScopeCatalogEntry[]} */
export const SCOPE_CATALOG = [
  {
    name: "dashboard.read",
    label: "Dashboard — read",
    description: "View / open this area.",
    affects: ["/", "Home dashboard"],
    area: "Dashboard",
    action: "read",
  },
  {
    name: "session.read",
    label: "Session — read",
    description: "Required for an authenticated session and basic protected API access. Not a single page — app-wide.",
    affects: ["App-wide (session / API)"],
    area: "Session",
    action: "read",
  },
  {
    name: "analytics.read",
    label: "Analytics — read",
    description: "View / open this area.",
    affects: ["/analytics"],
    area: "Analytics",
    action: "read",
  },
  {
    name: "reports.read",
    label: "Reports — read",
    description: "View / open this area.",
    affects: ["/reports", "/documentation"],
    area: "Reports",
    action: "read",
  },
  {
    name: "tickets.read",
    label: "Tickets — read",
    description: "View / open this area.",
    affects: ["/tickets", "Ticket detail"],
    area: "Tickets",
    action: "read",
  },
  {
    name: "tickets.create",
    label: "Tickets — create",
    description: "Create resources in this area.",
    affects: ["/tickets", "Ticket detail"],
    area: "Tickets",
    action: "create",
  },
  {
    name: "tickets.update",
    label: "Tickets — update",
    description: "Update resources in this area.",
    affects: ["/tickets", "Ticket detail"],
    area: "Tickets",
    action: "update",
  },
  {
    name: "discovery.read",
    label: "Discovery — read",
    description: "View / open this area.",
    affects: ["/discovery"],
    area: "Discovery",
    action: "read",
  },
  {
    name: "approvals.read",
    label: "Approvals — read",
    description: "View / open this area.",
    affects: ["/approvals", "/governance"],
    area: "Approvals",
    action: "read",
  },
  {
    name: "approvals.update",
    label: "Approvals — update",
    description: "Update resources in this area.",
    affects: ["/approvals", "/governance"],
    area: "Approvals",
    action: "update",
  },
  {
    name: "ffid.update",
    label: "FFID — update",
    description: "View and manage Firefighter ID tickets (list, detail, approve/reject, Human Escalation).",
    affects: ["/ffid", "/ffid/:id"],
    area: "FFID",
    action: "update",
  },
  {
    name: "transformations.read",
    label: "Transformations — read",
    description: "View Transformations dashboard, companies, studio, and project detail.",
    affects: [
      "/transformations",
      "/transformations/companies",
      "/transformations/studio",
      "/transformations/projects/:id",
    ],
    area: "Transformations",
    action: "read",
  },
  {
    name: "transformations.update",
    label: "Transformations — update",
    description:
      "Create/update/delete companies and projects, run analyze/chat, and change agent model prefs. Implies Transformations read.",
    affects: [
      "/transformations",
      "/transformations/companies",
      "/transformations/studio",
      "/transformations/projects/:id",
    ],
    area: "Transformations",
    action: "update",
  },
  {
    name: "trigger-workflow.read",
    label: "Trigger workflow — read",
    description: "View / open this area.",
    affects: ["/trigger-workflow"],
    area: "Trigger workflow",
    action: "read",
  },
  {
    name: "trigger-workflow.create",
    label: "Trigger workflow — create",
    description: "Create resources in this area.",
    affects: ["/trigger-workflow"],
    area: "Trigger workflow",
    action: "create",
  },
  {
    name: "errors.read",
    label: "Errors — read",
    description: "View / open this area.",
    affects: ["/errors"],
    area: "Errors",
    action: "read",
  },
  {
    name: "errors.update",
    label: "Errors — update",
    description: "Update resources in this area.",
    affects: ["/errors"],
    area: "Errors",
    action: "update",
  },
  {
    name: "deployment.read",
    label: "Deployment — read",
    description: "View / open this area.",
    affects: ["/deployment"],
    area: "Deployment",
    action: "read",
  },
  {
    name: "deployment.create",
    label: "Deployment — create",
    description: "Create resources in this area.",
    affects: ["/deployment"],
    area: "Deployment",
    action: "create",
  },
  {
    name: "deployment.update",
    label: "Deployment — update",
    description: "Update resources in this area.",
    affects: ["/deployment"],
    area: "Deployment",
    action: "update",
  },
  {
    name: "business-value.read",
    label: "Business value — read",
    description: "View / open this area.",
    affects: ["/business-value"],
    area: "Business value",
    action: "read",
  },
  {
    name: "business-value.create",
    label: "Business value — create",
    description: "Create resources in this area.",
    affects: ["/business-value"],
    area: "Business value",
    action: "create",
  },
  {
    name: "business-value.update",
    label: "Business value — update",
    description: "Update resources in this area.",
    affects: ["/business-value"],
    area: "Business value",
    action: "update",
  },
  {
    name: "business-value.delete",
    label: "Business value — delete",
    description: "Delete resources in this area.",
    affects: ["/business-value"],
    area: "Business value",
    action: "delete",
  },
  {
    name: "agent-studio.read",
    label: "Agent Studio — read",
    description: "View / open this area.",
    affects: ["/agent-studio"],
    area: "Agent Studio",
    action: "read",
  },
  {
    name: "agent-studio.create",
    label: "Agent Studio — create",
    description: "Create resources in this area.",
    affects: ["/agent-studio"],
    area: "Agent Studio",
    action: "create",
  },
  {
    name: "agent-studio.update",
    label: "Agent Studio — update",
    description: "Update resources in this area.",
    affects: ["/agent-studio"],
    area: "Agent Studio",
    action: "update",
  },
  {
    name: "agent-studio.delete",
    label: "Agent Studio — delete",
    description: "Delete resources in this area.",
    affects: ["/agent-studio"],
    area: "Agent Studio",
    action: "delete",
  },
  {
    name: "vibe-studio.read",
    label: "Vibe Studio — read",
    description: "View / open this area.",
    affects: ["/vibe-studio"],
    area: "Vibe Studio",
    action: "read",
  },
  {
    name: "vibe-studio.create",
    label: "Vibe Studio — create",
    description: "Create resources in this area.",
    affects: ["/vibe-studio"],
    area: "Vibe Studio",
    action: "create",
  },
  {
    name: "vibe-studio.update",
    label: "Vibe Studio — update",
    description: "Update resources in this area.",
    affects: ["/vibe-studio"],
    area: "Vibe Studio",
    action: "update",
  },
  {
    name: "settings.sap-landscape.read",
    label: "Settings · SAP landscape — read",
    description: "View / open this area.",
    affects: ["Settings → SAP Landscape"],
    area: "Settings · SAP landscape",
    action: "read",
  },
  {
    name: "settings.sap-landscape.create",
    label: "Settings · SAP landscape — create",
    description: "Create resources in this area.",
    affects: ["Settings → SAP Landscape"],
    area: "Settings · SAP landscape",
    action: "create",
  },
  {
    name: "settings.sap-landscape.update",
    label: "Settings · SAP landscape — update",
    description: "Update resources in this area.",
    affects: ["Settings → SAP Landscape"],
    area: "Settings · SAP landscape",
    action: "update",
  },
  {
    name: "settings.sap-landscape.delete",
    label: "Settings · SAP landscape — delete",
    description: "Delete resources in this area.",
    affects: ["Settings → SAP Landscape"],
    area: "Settings · SAP landscape",
    action: "delete",
  },
  {
    name: "settings.mcp-servers.read",
    label: "Settings · MCP servers — read",
    description: "View / open this area.",
    affects: ["Settings → MCP Servers"],
    area: "Settings · MCP servers",
    action: "read",
  },
  {
    name: "settings.mcp-servers.create",
    label: "Settings · MCP servers — create",
    description: "Create resources in this area.",
    affects: ["Settings → MCP Servers"],
    area: "Settings · MCP servers",
    action: "create",
  },
  {
    name: "settings.mcp-servers.update",
    label: "Settings · MCP servers — update",
    description: "Update resources in this area.",
    affects: ["Settings → MCP Servers"],
    area: "Settings · MCP servers",
    action: "update",
  },
  {
    name: "settings.mcp-servers.delete",
    label: "Settings · MCP servers — delete",
    description: "Delete resources in this area.",
    affects: ["Settings → MCP Servers"],
    area: "Settings · MCP servers",
    action: "delete",
  },
  {
    name: "settings.general.read",
    label: "Settings · General — read",
    description: "View Settings → General (platform settings not covered by SAP/MCP/users/groups).",
    affects: ["Settings → General"],
    area: "Settings · General",
    action: "read",
  },
  {
    name: "settings.general.update",
    label: "Settings · General — update",
    description: "Change Settings → General platform settings.",
    affects: ["Settings → General"],
    area: "Settings · General",
    action: "update",
  },
  {
    name: "settings.user-management.read",
    label: "Settings · User management — read",
    description: "View / open this area.",
    affects: ["Settings → User Management"],
    area: "Settings · User management",
    action: "read",
  },
  {
    name: "settings.user-management.create",
    label: "Settings · User management — create",
    description: "Create resources in this area.",
    affects: ["Settings → User Management"],
    area: "Settings · User management",
    action: "create",
  },
  {
    name: "settings.user-management.update",
    label: "Settings · User management — update",
    description: "Update resources in this area.",
    affects: ["Settings → User Management"],
    area: "Settings · User management",
    action: "update",
  },
  {
    name: "settings.user-management.delete",
    label: "Settings · User management — delete",
    description: "Delete users in Settings → User Management.",
    affects: ["Settings → User Management"],
    area: "Settings · User management",
    action: "delete",
  },
  {
    name: "settings.group-management.read",
    label: "Settings · Group management — read",
    description: "View / open this area.",
    affects: ["Settings → Group Management", "/settings/groups"],
    area: "Settings · Group management",
    action: "read",
  },
  {
    name: "settings.group-management.create",
    label: "Settings · Group management — create",
    description: "Create resources in this area.",
    affects: ["Settings → Group Management", "/settings/groups"],
    area: "Settings · Group management",
    action: "create",
  },
  {
    name: "settings.group-management.update",
    label: "Settings · Group management — update",
    description: "Update resources in this area.",
    affects: ["Settings → Group Management", "/settings/groups"],
    area: "Settings · Group management",
    action: "update",
  },
  {
    name: "settings.group-management.delete",
    label: "Settings · Group management — delete",
    description: "Delete resources in this area.",
    affects: ["Settings → Group Management", "/settings/groups"],
    area: "Settings · Group management",
    action: "delete",
  },
];

/** @type {Map<string, ScopeCatalogEntry>} */
const BY_NAME = new Map(SCOPE_CATALOG.map((e) => [e.name, e]));

/**
 * @param {string} name
 * @returns {ScopeCatalogEntry | undefined}
 */
export function getScopeCatalogEntry(name) {
  return BY_NAME.get(name);
}

/**
 * Popover / tooltip body for Group Management ⓘ control.
 * @param {string} name
 * @returns {string}
 */
export function formatScopeTooltip(name) {
  const e = BY_NAME.get(name);
  if (!e) return name;
  const affects = e.affects.join(", ");
  return `${e.description}\n\nAffects: ${affects}`;
}

/**
 * Scopes grouped for checkbox UI (preserve catalog order of first appearance).
 * @returns {{ area: string, scopes: ScopeCatalogEntry[] }[]}
 */
export function getScopeCatalogGroupedByArea() {
  /** @type {Map<string, ScopeCatalogEntry[]>} */
  const map = new Map();
  for (const e of SCOPE_CATALOG) {
    if (!map.has(e.area)) map.set(e.area, []);
    map.get(e.area).push(e);
  }
  return [...map.entries()].map(([area, scopes]) => ({ area, scopes }));
}

/** All scope short names in the catalog (source for Role CRUD checklists). */
export function getCatalogScopeNames() {
  return SCOPE_CATALOG.map((e) => e.name);
}
